import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  UserIcon,
  ShieldCheckIcon,
  KeyIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { getFirestore, collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where } from 'firebase/firestore';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { sendPasswordReset, generateRandomPassword, createAuthAccountAndSendReset } from '../utils/passwordUtils';

const RoleManagement = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showUserRoleModal, setShowUserRoleModal] = useState(false);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [passwordResetEmail, setPasswordResetEmail] = useState('');

  // Available permissions for the application - matching dashboard tiles and sidebar menu
  const availablePermissions = {
    // Quick Actions Section
    quickActions: {
      analyticsHub: 'Analytics Hub',
      allReservations: 'All Reservations',
      totalEarnings: 'Total Earnings',
      pendingReviews: 'Pending Reviews',
      unpaidInvoices: 'Unpaid Invoices'
    },
    // User Management Section
    userManagement: {
      parkingUsers: 'Parking Users',
      manualUserList: 'Manual User List',
      manualBusinessList: 'Business List & WhatsApp',
      parkingOwners: 'Parking Owners',
      partners: 'Partners',
      mealVendors: 'Kerblet MealDesk Vendors',
      mealCustomers: 'Kerblet MealDesk Customers',
      roleManagement: 'Role Management'
    },
    // Reservations Section
    reservations: {
      reservedReservations: 'Reserved (Confirmation Pending)',
      confirmedReservations: 'Confirmed (Checkin Pending)',
      activeReservations: 'Active Reservations',
      completedReservations: 'Completed Reservations',
      cancelledReservations: 'Cancelled'
    },
    // Parking Management Section
    parkingManagement: {
      totalSpaces: 'Total Spaces',
      availableSpaces: 'Available Spaces',
      disabledSpaces: 'Disabled Spaces'
    },
    // Financial Section
    financial: {
      totalTransactions: 'Total Transactions',
      totalInvoices: 'Total Invoices',
      totalEarnings: 'Total Earnings'
    },
    // Pass Management Section
    passManagement: {
      totalPasses: 'Total Passes',
      multiUsePasses: 'Multi-use Passes',
      oneTimePasses: 'One-time Passes',
      partnerDiscountCoupons: 'Partner Discount Coupons',
      perksDealVouchers: 'Perks Deal Vouchers'
    },
    // Operations Section
    operations: {
      mealDeskSchedule: 'Kerblet MealDesk Schedule',
      fieldDayPlanner: 'Field Day Planner'
    },
    // Sidebar Menu Items
    sidebarMenu: {
      dashboard: 'Dashboard',
      userManagement: 'User Management',
      analytics: 'Analytics',
      settings: 'Settings',
      notifications: 'Notifications'
    }
  };

  const [roleForm, setRoleForm] = useState({
    name: '',
    description: '',
    permissions: {}
  });

  const [userRoleForm, setUserRoleForm] = useState({
    userId: '',
    roleId: ''
  });

  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({
    username: '',
    firstname: '',
    lastname: '',
    email: '',
    password: '',
    roleId: ''
  });

  useEffect(() => {
    loadRoles();
    loadUsers();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      const rolesRef = collection(db, 'roles');
      const querySnapshot = await getDocs(rolesRef);
      
      const rolesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setRoles(rolesData);
    } catch (err) {
      setError('Failed to load roles');
      console.error('Error loading roles:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const db = getFirestore();
      const managersRef = collection(db, 'managers');
      const querySnapshot = await getDocs(managersRef);
      
      const usersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setUsers(usersData);
    } catch (err) {
      console.error('Error loading managers:', err);
    }
  };

  const handleRoleFormChange = (field, value) => {
    setRoleForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePermissionChange = (category, permission, checked) => {
    setRoleForm(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [category]: {
          ...prev.permissions[category],
          [permission]: checked
        }
      }
    }));
  };

  const handleSaveRole = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      
      if (editingRole) {
        // Update existing role
        await updateDoc(doc(db, 'roles', editingRole.id), {
          ...roleForm,
          updatedAt: new Date()
        });
      } else {
        // Create new role
        await addDoc(collection(db, 'roles'), {
          ...roleForm,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }
      
      setShowRoleModal(false);
      setEditingRole(null);
      setRoleForm({ name: '', description: '', permissions: {} });
      await loadRoles();
    } catch (err) {
      setError('Failed to save role');
      console.error('Error saving role:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (window.confirm('Are you sure you want to delete this role?')) {
      try {
        setLoading(true);
        const db = getFirestore();
        await deleteDoc(doc(db, 'roles', roleId));
        await loadRoles();
      } catch (err) {
        setError('Failed to delete role');
        console.error('Error deleting role:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditRole = (role) => {
    setEditingRole(role);
    setRoleForm({
      name: role.name,
      description: role.description,
      permissions: role.permissions || {}
    });
    setShowRoleModal(true);
  };

  const handleAssignUserRole = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      
      // Update manager with role
      await updateDoc(doc(db, 'managers', userRoleForm.userId), {
        roleId: userRoleForm.roleId,
        updatedAt: new Date()
      });
      
      setShowUserRoleModal(false);
      setUserRoleForm({ userId: '', roleId: '' });
      setSelectedUser(null);
      await loadUsers();
    } catch (err) {
      setError('Failed to assign role to manager');
      console.error('Error assigning role:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      const auth = getAuth();
      
      // First, create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        createUserForm.email,
        createUserForm.password
      );
      
      const firebaseUser = userCredential.user;
      
      // Then create manager record in Firestore
      const newManager = {
        username: createUserForm.username,
        firstname: createUserForm.firstname,
        lastname: createUserForm.lastname,
        email: createUserForm.email,
        password: createUserForm.password,
        roleId: createUserForm.roleId || null,
        addManager: false,
        changeFree: false,
        changeSetting: false,
        firebaseUid: firebaseUser.uid, // Store Firebase UID for reference
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      await addDoc(collection(db, 'managers'), newManager);
      
      setShowCreateUserModal(false);
      setCreateUserForm({
        username: '',
        firstname: '',
        lastname: '',
        email: '',
        password: '',
        roleId: ''
      });
      await loadUsers();
    } catch (err) {
      setError('Failed to create manager');
      console.error('Error creating manager:', err);
    } finally {
      setLoading(false);
    }
  };

  const getPermissionCount = (permissions) => {
    let count = 0;
    Object.values(permissions).forEach(category => {
      if (typeof category === 'object') {
        Object.values(category).forEach(permission => {
          if (permission) count++;
        });
      }
    });
    return count;
  };

  const handlePasswordReset = async (userEmail) => {
    setPasswordResetEmail(userEmail);
    setShowPasswordResetModal(true);
  };

  const confirmPasswordReset = async () => {
    try {
      setLoading(true);
      console.log('Starting password reset for:', passwordResetEmail);
      
      // Generate a temporary password
      const tempPassword = generateRandomPassword(12);
      console.log('Generated temporary password:', tempPassword);
      
      // Create Firebase Auth account if needed and send password reset
      const result = await createAuthAccountAndSendReset(passwordResetEmail, tempPassword);
      console.log('Password reset result:', result);
      
      if (result.success) {
        alert('Password reset email sent successfully to ' + passwordResetEmail + '\n\nIf a new Firebase Auth account was created, the user will receive a password reset email to set their password.');
      } else {
        alert('Failed to send password reset email: ' + result.message);
      }
    } catch (error) {
      console.error('Error sending password reset:', error);
      alert('Failed to send password reset email: ' + error.message);
    } finally {
      setLoading(false);
      setShowPasswordResetModal(false);
    }
  };

  if (loading && roles.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-full mx-auto px-2 sm:px-4 lg:px-6">
                      <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/')}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
                </button>
                <h1 className="text-2xl font-semibold text-gray-900">Role Management</h1>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowCreateUserModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <UserIcon className="h-4 w-4 mr-2" />
                  Create Manager
                </button>
                <button
                  onClick={() => setShowRoleModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Create Role
                </button>
              </div>
            </div>
        </div>
      </div>

      <div className="max-w-full mx-auto px-2 sm:px-4 lg:px-6 py-6">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Roles Section */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Roles & Permissions</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Permissions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {roles.map((role) => (
                  <tr key={role.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {role.name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {role.description}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {getPermissionCount(role.permissions || {})} permissions
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditRole(role)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteRole(role.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Manager Role Assignment Section */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Manager Role Assignment</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Manager
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Username
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => {
                  const userRole = roles.find(role => role.id === user.roleId);
                  return (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {user.firstname} {user.lastname}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.username}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {userRole ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {userRole.name}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            No Role
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setUserRoleForm({ userId: user.id, roleId: user.roleId || '' });
                              setShowUserRoleModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                            title="Assign Role"
                          >
                            <KeyIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handlePasswordReset(user.email)}
                            className="text-orange-600 hover:text-orange-900"
                            title="Send Password Reset Email"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create/Edit Role Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingRole ? 'Edit Role' : 'Create New Role'}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role Name</label>
                  <input
                    type="text"
                    value={roleForm.name}
                    onChange={(e) => handleRoleFormChange('name', e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter role name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={roleForm.description}
                    onChange={(e) => handleRoleFormChange('description', e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                    placeholder="Enter role description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {Object.entries(availablePermissions).map(([category, permissions]) => (
                      <div key={category} className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-3 border-b border-gray-200 pb-2">
                          {category === 'quickActions' && '🚀 Quick Actions'}
                          {category === 'userManagement' && '👥 User Management'}
                          {category === 'reservations' && '📅 Reservations'}
                          {category === 'parkingManagement' && '🅿️ Parking Management'}
                          {category === 'financial' && '💰 Financial'}
                          {category === 'passManagement' && '🎫 Pass Management'}
                          {category === 'operations' && '⚙️ Operations'}
                          {category === 'sidebarMenu' && '📋 Sidebar Menu'}
                        </h4>
                        <div className="grid grid-cols-1 gap-2">
                          {Object.entries(permissions).map(([permission, label]) => (
                            <label key={permission} className="flex items-center space-x-2 hover:bg-gray-50 p-1 rounded">
                              <input
                                type="checkbox"
                                checked={roleForm.permissions[category]?.[permission] || false}
                                onChange={(e) => handlePermissionChange(category, permission, e.target.checked)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">{label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowRoleModal(false);
                    setEditingRole(null);
                    setRoleForm({ name: '', description: '', permissions: {} });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveRole}
                  disabled={!roleForm.name.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {editingRole ? 'Update Role' : 'Create Role'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign User Role Modal */}
      {showUserRoleModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Assign Role to Manager
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Manager</label>
                  <div className="mt-1 p-3 bg-gray-50 rounded-md">
                    <p className="text-sm font-medium text-gray-900">
                      {selectedUser?.firstname} {selectedUser?.lastname}
                    </p>
                    <p className="text-sm text-gray-500">{selectedUser?.email}</p>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  <select
                    value={userRoleForm.roleId}
                    onChange={(e) => setUserRoleForm(prev => ({ ...prev, roleId: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a role</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowUserRoleModal(false);
                    setUserRoleForm({ userId: '', roleId: '' });
                    setSelectedUser(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignUserRole}
                  disabled={!userRoleForm.roleId}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  Assign Role
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Confirmation Modal */}
      {showPasswordResetModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Send Password Reset Email
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email Address</label>
                  <div className="mt-1 p-3 bg-gray-50 rounded-md">
                    <p className="text-sm font-medium text-gray-900">{passwordResetEmail}</p>
                  </div>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <p className="text-sm text-blue-800">
                    This will:
                  </p>
                  <ul className="text-sm text-blue-700 mt-2 list-disc list-inside">
                    <li>Create a Firebase Auth account if one doesn't exist</li>
                    <li>Send a password reset email to the user</li>
                    <li>Allow the user to set their own password</li>
                  </ul>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowPasswordResetModal(false);
                    setPasswordResetEmail('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmPasswordReset}
                  disabled={loading}
                  className="px-4 py-2 bg-orange-600 text-white rounded-md text-sm font-medium hover:bg-orange-700 disabled:opacity-50"
                >
                  {loading ? 'Sending...' : 'Send Reset Email'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Manager Modal */}
      {showCreateUserModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Create New Manager
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Username (Auto-set from Email)</label>
                  <input
                    type="text"
                    value={createUserForm.username}
                    readOnly
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500 cursor-not-allowed"
                    placeholder="Will be set automatically from email"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">First Name</label>
                  <input
                    type="text"
                    value={createUserForm.firstname}
                    onChange={(e) => setCreateUserForm(prev => ({ ...prev, firstname: e.target.value }))}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter first name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Last Name</label>
                  <input
                    type="text"
                    value={createUserForm.lastname}
                    onChange={(e) => setCreateUserForm(prev => ({ ...prev, lastname: e.target.value }))}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter last name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    value={createUserForm.email}
                    onChange={(e) => {
                      const email = e.target.value;
                      setCreateUserForm(prev => ({ 
                        ...prev, 
                        email: email,
                        username: email // Auto-set username to email
                      }));
                    }}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter email"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  <input
                    type="password"
                    value={createUserForm.password}
                    onChange={(e) => setCreateUserForm(prev => ({ ...prev, password: e.target.value }))}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter password"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role (Optional)</label>
                  <select
                    value={createUserForm.roleId}
                    onChange={(e) => setCreateUserForm(prev => ({ ...prev, roleId: e.target.value }))}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a role (optional)</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreateUserModal(false);
                    setCreateUserForm({
                      username: '',
                      firstname: '',
                      lastname: '',
                      email: '',
                      password: '',
                      roleId: ''
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateUser}
                  disabled={!createUserForm.firstname || !createUserForm.lastname || !createUserForm.email || !createUserForm.password}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  Create Manager
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManagement; 